import { useCallback } from 'react';

import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { useHistory } from 'src/lib/hooks';
import { captureMessage } from 'src/lib/sentry';
import { ROUTES } from 'src/routes';
import { useDeleteWorkspace, useRestrictActionPopup } from 'src/services';
import {
  useCustomerActions,
  useCustomerCanCreateFeatureItem,
  useWorkspaceActions,
} from 'src/store';
import { useUpgradePopup } from 'src/views/UpgradePopup';
import { useWorkspacePopup } from 'src/views/WorkspacePopup';

export const useHandlers = ({ closeMenu }) => {
  const customerActions = useCustomerActions();
  const openWorkspacePopup = useWorkspacePopup();
  const deleteWorkspace = useDeleteWorkspace();
  const workspaceStoreActions = useWorkspaceActions();
  const openUpgradePopup = useUpgradePopup();
  const openRestrictActionPopup = useRestrictActionPopup();
  const history = useHistory();

  const customerCanCreateWorkspaces = useCustomerCanCreateFeatureItem(
    FEATURE_LIMITS.workspacesPerCustomer
  );

  const handleMenuClose = useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  const handleDeleteWorkspace = useCallback(
    (workspace) => {
      handleMenuClose();
      deleteWorkspace(workspace);
    },
    [handleMenuClose, deleteWorkspace]
  );

  const handleWorkspaceClick = useCallback(
    async (workspace) => {
      handleMenuClose();
      try {
        await workspaceStoreActions.selectCurrentWorkspace(workspace.id);
      } catch (error) {
        captureMessage("Couldn't select workspace", {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'WorkspaceItem -> handleWorkspaceClick',
          },
        });
      }
      customerActions.refreshCurrentCustomerFeatureLimit();
      history.push(ROUTES.ROOT);
    },
    [handleMenuClose, workspaceStoreActions, customerActions, history]
  );

  const handleCreateOrUpdateWorkspace = useCallback(
    (workspace) => {
      handleMenuClose();

      // If this is "creation", check if it's allowed
      if (!workspace) {
        if (!customerActions.getIsCurrentUserAdmin()) {
          openRestrictActionPopup();
          return;
        }
        if (!customerCanCreateWorkspaces) {
          openUpgradePopup(FEATURE_LIMITS.workspacesPerCustomer);
          return;
        }
      }

      openWorkspacePopup(workspace);
    },
    [
      customerActions,
      customerCanCreateWorkspaces,
      handleMenuClose,
      openWorkspacePopup,
      openRestrictActionPopup,
      openUpgradePopup,
    ]
  );

  return {
    handleMenuClose,
    handleWorkspaceClick,
    handleCreateOrUpdateWorkspace,
    handleDeleteWorkspace,
  };
};
